/**
 * Los selects de ubicación guardan el nombre, pero muestran "id-nombre".
 *
 * Departamento y municipio se guardan solo con su nombre —"ANTIOQUIA",
 * "MEDELLÍN"—, mientras que cada opción del select vale "05-ANTIOQUIA". Al
 * volver a entrar, el valor guardado no coincidía con ninguna opción y el
 * campo salía en blanco, como si no se hubiera diligenciado nunca.
 *
 * Aquí se traduce lo guardado al valor que el select reconoce. Si no se
 * encuentra en la lista se devuelve tal cual: es mejor mostrar algo que el
 * campo vacío.
 */

export type Ubicacion = { id: any; nombre: string };

/** El valor "id-nombre" que corresponde a ese nombre, buscándolo en la lista. */
export const valorConId = (lista: any, nombre?: string): string | undefined => {
  const texto = String(nombre ?? '').trim();
  if (!texto) return undefined;

  const opciones: Ubicacion[] = Array.isArray(lista) ? lista : [];
  const hallado = opciones.find((x) => x?.nombre === texto);

  return hallado ? `${hallado.id}-${hallado.nombre}` : texto;
};

/** El id que lleva adentro un valor "id-nombre", o null si no lo trae. */
export const idDelValor = (valor: any): string | null => {
  const texto = String(valor ?? '').trim();
  if (!texto) return null;

  const [id] = texto.split('-');
  return id || null;
};

/**
 * El nombre tal como lo escribe la lista, buscándolo sin reparar en mayúsculas.
 *
 * El barrio se guarda en mayúsculas —"BOSTON"— pero la opción del select vale
 * lo que trae el servicio —"Boston"—, así que el valor guardado no coincidía
 * con ninguna opción y el campo salía vacío. Si no se encuentra se devuelve
 * lo guardado: mejor mostrar algo que nada.
 */
export const nombreEnLista = (lista: any, nombre?: string): string | undefined => {
  const texto = String(nombre ?? '').trim();
  if (!texto) return undefined;

  const opciones: Ubicacion[] = Array.isArray(lista) ? lista : [];
  const hallado = opciones.find(
    (x) => String(x?.nombre ?? '').toUpperCase() === texto.toUpperCase()
  );

  return hallado ? hallado.nombre : texto;
};

/**
 * La comuna puede venir compuesta —"10-La Candelaria"— o solo con su número,
 * según cómo se haya guardado. Se devuelve siempre compuesta.
 */
export const valorDeComuna = (
  comunas: any,
  comuna?: string
): string | undefined => {
  const texto = String(comuna ?? '').trim();
  if (!texto) return undefined;
  if (texto.includes('-')) return texto;

  const opciones: { key: any; value: string }[] = Array.isArray(comunas) ? comunas : [];
  const hallada = opciones.find((c) => String(c?.key) === texto);

  return hallada ? `${hallada.key}-${hallada.value}` : texto;
};
